import { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { filterUserInput } from "app/components/React/list-editor/list-editor.utils";
import type { AutoComplete } from "app/components/React/Tooltip/AutoComplete/AutoComplete";
import { CircularLoader } from "app/components/React/CircularLoader/CircularLoader";
import { ListItems } from "app/components/React/list-editor/ListItems";
import { ListItemsInput } from "app/components/React/list-editor/ListItemsInput";
import { i18nFilter } from "app/filters/i18nFilter";
import { KeywordListItem } from "./KeywordListItem";
import type { NewGroupTracker } from "./KeywordsGroupsTracking";
import { allTrackers } from "app/services/track/track";

const ListItemsWrapper = styled.div`
    height: calc(100% - 52px);
    padding: 0 8px 0 16px;
`;

const Loader = (
    <CircularLoader
        options={{
            svg: {
                stroke: "#dedede",
                strokeWidth: "4",
                r: 21,
                cx: "50%",
                cy: "50%",
            },
            style: {
                width: 46,
                height: 46,
                position: "absolute",
                top: "calc(50% - 23px)",
                left: "calc(50% - 23px)",
            },
        }}
    />
);

export type KeywordGroupEditorRef = {
    focusInput(): void;
    getListElement(): HTMLElement | null;
    onAddItem(test: string, trackAction?: boolean): void;
};

interface IKeywordGroupEditorProps {
    keywords: string[];
    tracker: NewGroupTracker;
    maxItems: number;
    footer?: React.ReactNode;
    onKeywordListChange: (keywords: string[]) => void;
    setRef: (ref: KeywordGroupEditorRef) => void;
    onError: (error: string) => void;
}

export const KeywordGroupEditor = ({
    keywords = [],
    tracker,
    maxItems,
    footer,
    onKeywordListChange,
    setRef,
    onError,
}: IKeywordGroupEditorProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const items = keywords.map((kw) => ({ text: kw.toLowerCase() }));
    const itemsInputRef = useRef<AutoComplete>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setRef({
            focusInput: () => itemsInputRef.current?.inputElem?.focus(),
            getListElement: () => listRef.current,
            onAddItem,
        });
    }, [setRef]);

    const validateExistingItem = (index: number) => index < maxItems;

    const isItemValid = (text: string) => {
        return text.length > 0 && text.length <= 140;
    };

    const isDuplicateItem = (text: string, list: typeof items) => {
        return list.some((i) => i.text.toLocaleLowerCase() === text.toLocaleLowerCase());
    };

    const isItemIndexLowerThanMaxItems = (item: string, list: typeof items) => {
        const isListFull = list.length >= maxItems;
        const isItemLengthValid = isItemValid(item);
        const isDuplicate = isDuplicateItem(item, list);
        const isValid = !isListFull && isItemLengthValid && !isDuplicate;
        if (isListFull) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.list.is.full");
        } else if (!isItemLengthValid) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.item.length");
        } else if (isDuplicate) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.already.exists");
        }
        return { isValid };
    };

    const filterPastedItems = (itemsToAdd: string[], itemsList: typeof items) => {
        const newListItems = itemsToAdd.filter((itemToAdd) => {
            const { isValid } = isItemIndexLowerThanMaxItems(itemToAdd, itemsList);
            if (isValid) {
                itemsList = [...itemsList, { text: itemToAdd }];
            }
            return isValid;
        });
        if (itemsToAdd.length === 0) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.invalid.paste");
        } else if (newListItems.length === 0) {
            const isFullList = itemsList.length >= maxItems;
            if (isFullList) {
                onError("KeywordAnalysis.keywordgroup.wizard.error.list.is.full");
            } else {
                onError("KeywordAnalysis.keywordgroup.wizard.error.already.exists.or.invalid");
            }
        } else if (newListItems.length < itemsToAdd.length) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.invalid.keywords.removed");
        }

        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Paste Keywords");
        return Promise.resolve({
            newListItems,
            isValid: !!newListItems.length,
        });
    };

    const removeItem = (itemToRemove: (typeof items)[number]) => {
        onKeywordListChange(keywords.filter((item) => item.toLowerCase() !== itemToRemove.text.toLowerCase()));
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Remove keyword");
    };

    const onItemTextChanged = (item: (typeof items)[number], newText: string) => {
        if (item.text === newText) {
            return;
        }

        if (isDuplicateItem(newText, items)) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.already.exists");
            return;
        }

        if (!isItemValid(newText)) {
            onError("KeywordAnalysis.keywordgroup.wizard.error.item.length");
            return;
        }

        const sanitizedInput = filterUserInput(newText);

        const itemIndex = items.findIndex((i) => i.text === item.text);
        onKeywordListChange([...keywords.slice(0, itemIndex), sanitizedInput, ...keywords.slice(itemIndex + 1)]);
        tracker.onEdit({ ...item, text: sanitizedInput });
    };

    const onAddItem = (text, trackAction = true) => {
        if (!isItemIndexLowerThanMaxItems(text, items).isValid) {
            return;
        }
        if (text.split(/\s*,\s*/)?.length > 1) {
            return onPaste(text);
        } else {
            onKeywordListChange([...keywords, text]);
        }
        if (trackAction) {
            tracker.onAdd({ text });
        }
    };

    const onPaste = (rawText: string) => {
        setIsLoading(true);
        setTimeout(async () => {
            const itemsFromPaste = rawText
                .trim()
                .split(/\r\n?|\n|\s*,\s*/)
                .slice(0, maxItems)
                .filter((item) => item?.length > 0)
                .map((item) => item.trim())
                .sort();
            const filteredItemsToPaste = await filterPastedItems(itemsFromPaste, items);
            tracker.onPaste(filteredItemsToPaste.newListItems);
            onKeywordListChange([...keywords, ...filteredItemsToPaste.newListItems]);
            setIsLoading(false);
        }, 500);
    };

    return (
        <div className="items-editor-box">
            <ListItemsInput
                ref={itemsInputRef}
                itemsListTypeKey={i18nFilter()("KeywordAnalysis.keywordgroup.wizard.list.type")}
                onPaste={onPaste}
                onAddItem={onAddItem}
            />
            <ListItemsWrapper>
                <ListItems
                    ref={listRef}
                    items={items}
                    removeItem={removeItem}
                    validateExistingItem={validateExistingItem}
                    onItemTextChanged={onItemTextChanged}
                    ListItemComponent={KeywordListItem}
                    itemHeight={44}
                    footer={footer}
                />
            </ListItemsWrapper>
            {isLoading ? Loader : null}
        </div>
    );
};
